/**
 * DriftTable: cloud vs on-device config per type, with optional rules-engine detail in expand.
 * Main table is customer-oriented (cloud vs device + status). Activated / raw JSON live under expand.
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Box,
  Typography,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import type { ConfigDriftResponse, TypeDrift } from '../services/configDrift';

function hashShort(h: string | undefined | null): string {
  if (!h || typeof h !== 'string') return '—';
  const s = String(h).trim();
  if (s.startsWith('sha256:')) return s.slice(7, 19) + '…';
  return s.length > 12 ? s.slice(0, 12) + '…' : s;
}

function formatUtc(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' Z');
}

function ButtonCopyRow({ label, text }: { label: string; text: string }) {
  return (
    <Button
      size="small"
      variant="text"
      sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: '0.7rem' }}
      startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
      onClick={() => navigator.clipboard?.writeText(text).catch(() => {})}
    >
      {label}
    </Button>
  );
}

function HashWithCopy({ full }: { full: string | undefined | null }) {
  if (!full) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const s = String(full).trim();
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
      <Tooltip title={s}>
        <Typography variant="caption" component="span">
          <code>{hashShort(s)}</code>
        </Typography>
      </Tooltip>
      <Tooltip title="Copy full hash">
        <IconButton size="small" aria-label="Copy hash" onClick={() => navigator.clipboard?.writeText(s).catch(() => {})}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  switch (String(status || '').toLowerCase()) {
    case 'converged':
      return 'success';
    case 'drifted':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

interface DriftTableProps {
  data: ConfigDriftResponse;
}

export const DriftTable: React.FC<DriftTableProps> = ({ data }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { types, stale_threshold_minutes } = data;

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const rows = Object.entries(types || {});

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell>Config</TableCell>
            <TableCell>In cloud (desired)</TableCell>
            <TableCell>On device (deploy / applied)</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([configType, t]) => {
            const typeData = t as TypeDrift;
            const key = configType;
            const isExpanded = expanded.has(key);
            const desired = typeData.desired;
            const applied = typeData.applied;
            const activated = typeData.activated;
            const notConfigured =
              typeData.status === 'unknown' &&
              String(typeData.reason_code || '').toUpperCase() === 'NO_DESIRED_DEPLOYMENT';
            const statusLabel = notConfigured ? 'not configured' : typeData.status;
            const hashMismatch = typeData.hash_match === false;
            const appliedNotActivated =
              applied &&
              activated &&
              (applied.bundle_hash || '') !== (activated.bundle_hash || '');
            const stale =
              typeData.stale_minutes != null &&
              typeData.stale_minutes > stale_threshold_minutes &&
              typeData.status === 'drifted';
            const showActivationLagChip =
              appliedNotActivated && typeData.status !== 'converged';
            const unknown = typeData.status === 'unknown';

            return (
              <React.Fragment key={key}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => toggleExpand(key)} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {configType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {desired ? (
                      <Typography variant="caption" component="div">
                        <HashWithCopy full={desired.bundle_hash} /> v{desired.bundle_version}
                        {desired.published_at && (
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                            {formatUtc(desired.published_at)}
                          </Typography>
                        )}
                        {desired.deployment_id && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            dep <code>{desired.deployment_id}</code>
                          </Typography>
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {applied ? (
                      <Typography variant="caption" component="div">
                        <HashWithCopy full={applied.bundle_hash} /> v{applied.bundle_version}
                        {applied.applied_at && (
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                            {formatUtc(applied.applied_at)}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={applied.source}
                          sx={{ mt: 0.25, height: 18, fontSize: '0.7rem' }}
                          variant="outlined"
                        />
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel}
                      color={statusColor(typeData.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {hashMismatch && (
                        <Chip size="small" label="Hash mismatch" color="error" variant="outlined" />
                      )}
                      {showActivationLagChip && (
                        <Chip
                          size="small"
                          label="Rules engine behind device"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {stale && (
                        <Chip size="small" label={`Stale ${Math.round(typeData.stale_minutes!)} min`} color="warning" variant="outlined" />
                      )}
                      {unknown && (
                        <Chip
                          size="small"
                          label={notConfigured ? 'Not configured' : 'Unknown'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? 1 : 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1, px: 2 }}>
                        <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                          Troubleshooting (deploy vs live)
                        </Typography>
                        {appliedNotActivated && typeData.status === 'converged' && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Deploy path matches the cloud. Different hashes below mean the live rules engine has not
                            caught up yet — usually brief. Converge fixes cloud↔deploy sync, not an in-process reload
                            timeline.
                          </Typography>
                        )}
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                            Deploy path (applied)
                          </Typography>
                          {applied ? (
                            <>
                              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {applied.bundle_hash} · v{applied.bundle_version}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                applied_at UTC {formatUtc(applied.applied_at)} · source {applied.source}
                              </Typography>
                              <ButtonCopyRow
                                label="Copy deploy line"
                                text={`applied hash=${applied.bundle_hash} v=${applied.bundle_version} at_utc=${formatUtc(applied.applied_at)} source=${applied.source}`}
                              />
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                            Live rules engine (activated / health)
                          </Typography>
                          {activated ? (
                            <>
                              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {activated.bundle_hash} · v{activated.bundle_version}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                activated_at UTC {formatUtc(activated.activated_at)} · source {activated.source || '—'}
                              </Typography>
                              <ButtonCopyRow
                                label="Copy live line"
                                text={`activated hash=${activated.bundle_hash} v=${activated.bundle_version} at_utc=${formatUtc(activated.activated_at)} source=${activated.source || '—'}`}
                              />
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No activation snapshot for this type (normal for some config types).
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" fontWeight={500}>
                            Raw JSON
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              copyToClipboard(
                                JSON.stringify(
                                  { desired: typeData.desired, applied: typeData.applied, activated: typeData.activated },
                                  null,
                                  2
                                )
                              )
                            }
                            aria-label="Copy"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            bgcolor: 'action.hover',
                            p: 1,
                            borderRadius: 1,
                            overflow: 'auto',
                            maxHeight: 200,
                          }}
                        >
                          {JSON.stringify(
                            { desired: typeData.desired, applied: typeData.applied, activated: typeData.activated },
                            null,
                            2
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
